import React, {useRef, Fragment, useEffect, useMemo, useState, useCallback} from 'react';
import {Vector3, Quaternion, Color} from 'three';
// import {Plane as THREEPlane} from 'three';
import {extend, useThree} from '@react-three/fiber';
import {TransformControls} from '@react-three/drei';

extend({TransformControls});

export const Planes = (props) => {
  return props.planes.map(
      (n,i) => {
        if (n.show){
          let type = 'cart';
          if (n.type !== undefined){
            type = n.type;
          }          
          return (
            <Plane 
              key={i} 
              mode={props.mode} 
              dir={n.dir} 
              center={n.center}
              orien={n.orien}
              id={n.id}
              type={type}
              addNode={props.addNode} 
              enableOrbitControls={props.enableOrbitControls} 
              updateGrid={props.updateGrid}
              transformGridType={props.transformGridType}
              active={(props.activePlane && props.activePlaneId === n.id) ? true : false}
            />
          )
        }
        else {
          return null;
        }
      }
    )
}

function Plane(props) {

  //settings for cartesian grid
  const wid = 1000;
  const div = 6;

  //settings for polar grid
  const sectors = 16;
  const rings = 3;
  const radius = 500;
  const divisions = 64;

  // const locY = new Vector3(0,1,0);
  let pos = new Vector3();
  const center = new Vector3().fromArray(props.center);
  
  let gridOrientation = new Quaternion();
  if (props.orien !== undefined){
    gridOrientation.fromArray(props.orien);
  }
  else {
    const dir = new Vector3().fromArray(props.dir);
    gridOrientation.setFromUnitVectors(new Vector3(0,1,0), dir); //(0,1,0) is default of gridHelper
  }
  
  const transformControlsRef = useRef();
  const gridRef = useRef();
  const {invalidate} = useThree();

  useEffect( ()=>{

        if (transformControlsRef.current) {
          const controls = transformControlsRef.current;
          
          const callbackOrbitControls = (e) => {
            props.enableOrbitControls(!e.value)
          };

          const callbackNewPlanePose = (e) => {
            if (props.transformGridType === 'translate'){
              gridRef.current.getWorldPosition(pos);
              props.updateGrid(props.id,pos,gridOrientation);
            } 
            if (props.transformGridType === 'rotate'){
              // let newDir = gridRef.current.localToWorld(locY).sub(center);
              let newOrienMatrix4 = gridRef.current.matrixWorld;
              gridOrientation.setFromRotationMatrix(newOrienMatrix4);
              props.updateGrid(props.id,center,gridOrientation);
            }         
            
          };

          controls.addEventListener('dragging-changed', callbackOrbitControls)
          controls.addEventListener('mouseUp', callbackNewPlanePose)
          controls.addEventListener('change', invalidate)

          return () => {
            controls.removeEventListener('dragging-changed', callbackOrbitControls)
            controls.removeEventListener('mouseUp', callbackNewPlanePose)
            controls.removeEventListener('change', invalidate)
          }
        }
      }
    )

  // const mainColor = new Color(0xb0b0b0);
  // const centerColor = new Color(0xb0b0b0);
  // let hoverColor = new Color('red');

  let mainColor, centerColor;
  (props.mode === 'planes' && props.active) ? mainColor = new Color(0x232323) : mainColor = new Color(0xb0b0b0);
  (props.mode === 'planes' && props.active) ? centerColor = new Color("black") : centerColor = new Color(0xb0b0b0);

  // attempt at hover colors for the grid plane -- not working great together with the transformcontrols hover...
  // const [hovered, setHover] = useState(false);
  
  // const hoverOver = (e) => {
  //   e.stopPropagation();
  //   if(!props.active){ gridRef.current.material.color = hoverColor }
  // }
  // const hoverOut = (e) => {
  //   if(!props.active){ gridRef.current.material.color = mainColor }
  // }

  // let gridHelperOut = <gridHelper args={[wid,div,centerColor,mainColor]} ref={gridRef} onPointerOver={hoverOver} onPointerOut={hoverOut} />;

  let gridHelperOut = [];
  if (props.type === 'polar'){
    gridHelperOut = <polarGridHelper args={[radius,sectors,rings,divisions,centerColor,mainColor]} ref={gridRef} />
  }
  else {
    //the default
    gridHelperOut = <gridHelper args={[wid,div,centerColor,mainColor]} ref={gridRef} />
  }

  let gridPointsOut = <GridPoints type={props.type} wid={wid} div={div} radius={radius} sectors={sectors} rings={rings} addNode={props.addNode} id={props.id} />;
  let gridBox;

  if (props.mode === 'planes' && props.active){
    gridBox = (
      <TransformControls 
        position={center} 
        quaternion={gridOrientation} 
        ref={transformControlsRef} 
        mode={props.transformGridType} 
        showY={props.transformGridType === 'translate' ? true : true} 
        space="local"
      >
        { gridHelperOut };
      </TransformControls>
    );    

  } 
  else if (props.mode === 'nodes') {
    gridBox = (
      <group position={center} quaternion={gridOrientation}>
        { gridHelperOut };
        { gridPointsOut };
      </group>
    );   
  }
  else {
    gridBox = (
      <group position={center} quaternion={gridOrientation}>
        { gridHelperOut };
      </group>
    );   
  }

  return gridBox;
}

function GridPoints(props) {
  const type = props.type;

  // https://codesandbox.io/s/eager-noyce-6rvwr?from-embed=&file=/src/index.js
  const div = props.div;
  const wid = props.wid;

  const rings = props.rings;
  const radius = props.radius;
  const sectors = props.sectors;

  const {raycaster} = useThree();
  raycaster.params.Points.threshold = wid/div/5;
  raycaster.params.Line.threshold = wid/div/5;
  // raycaster.params.Mesh.threshold = wid/div/5;
  
  const [hovered,setHover] = useState(false);
  const [hoverpos,setHoverpos] = useState([]);

  // const [positions, colors] = useMemo(() => {
  const positions = useMemo(() => {    
 
    let positions = [];
    // let colors = [];
    let k = 0;
    
    if(type === 'polar'){
      for (let i=1; i <= rings; i++){
        
        let localRadius = radius/rings*i;
        
        for (let j=0; j <= sectors; j++){
            let angle = j/sectors*2*Math.PI;
            positions[k] = localRadius*Math.cos(angle);
            positions[k+1] = 0;
            positions[k+2] = localRadius*Math.sin(angle);
            k = k+3;
        }

        //and the origin coordinate
        positions[k] = 0;
        positions[k+1] = 0;
        positions[k+2] = 0;
      }
    } 
    else { //the default: cartesian
      for (let i = 0; i <= div; i++) {

        const x = wid/div*i - wid/2;

        for (let j=0; j<=div; j++){
              positions[k] = x;
              positions[k+1] = 0;
              positions[k+2] = wid/div*j - wid/2;
              
              // colors.push(1)
              // colors.push(0.5)
              // colors.push(0.5)

              k = k+3;
        }
      }
    }

    // return [new Float32Array(positions), new Float32Array(colors)]
    return new Float32Array(positions);
  }, [type,div,wid,radius,rings,sectors])

  const pointsRef = useRef()

  const hover = useCallback(e => {
    e.stopPropagation()

    setHover(true)
    let pos = new Vector3(positions[e.index*3],positions[e.index*3+1], positions[e.index*3+2]);
    
    setHoverpos(pos)

  }, [positions])

  const isMounted = useRef(false);
  useEffect(() =>{
    isMounted.current = true;
    return () =>(isMounted.current=false);
  },[])

  const unhover = e => {
    // console.log(isMounted.current)
    isMounted.current && setHover(false)
  }

  const click = e => {
    let pos = new Vector3(positions[e.index*3],positions[e.index*3+1], positions[e.index*3+2]);
    pointsRef.current.localToWorld(pos)
    props.addNode([pos.x,pos.y,pos.z])
  }
  
  return (
    <Fragment>
    <points onPointerOver={hover} onPointerOut={unhover} onClick={click} visible={true} ref={pointsRef}>
      <bufferGeometry attach="geometry">
        <bufferAttribute attachObject={["attributes", "position"]} count={positions.length / 3} array={positions} itemSize={3} />
      </bufferGeometry>
      {/*<pointsMaterial color="red" size={50} />*/}
    </points>
    <mesh position={hoverpos} visible={hovered ? true : false}>
      <sphereGeometry args={[5]}/>
      <meshBasicMaterial color="red"/>
    </mesh>
    </Fragment>
  )
}